import React, { useContext, useEffect, useRef, useState } from 'react';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import type { JSX } from 'react';
import fileDownload from 'js-file-download';
import Typography from '@mui/material/Typography';
import type { IndexedDBContextProps } from '../../Contexts/IndexedDBContext';
import IndexedDBContext from '../../Contexts/IndexedDBContext';
import type BugReport from '../../Models/BugReportModel';
import { datetimeToVisualString } from '../../Utils/DateTimeParser';
import type { SystemContextProps } from '../../Contexts/SystemContext';
import SystemContext from '../../Contexts/SystemContext';
import { appVersion } from '../../Constants';
import { ErrorMessage, LoadingButton, SuccessMessage } from 'shouldve-been-mui';
import type { ApiContextProps } from '../../Contexts/ApiContext';
import ApiContext from '../../Contexts/ApiContext';
import type { TokenContextProps } from '../../Contexts/TokenContext';
import TokenContext from '../../Contexts/TokenContext';
import FormControl from '@mui/material/FormControl';
import Input from '@mui/material/Input';
import { handleTab } from 'shouldve-been-react';

type BugReportDialogProps = {
    controller: boolean,
    onClose: () => void,
};

function BugReportDialog(props: Readonly<BugReportDialogProps>): JSX.Element {

    const indexedDBContext: IndexedDBContextProps = useContext<IndexedDBContextProps>(IndexedDBContext);
    const systemContext: SystemContextProps = useContext<SystemContextProps>(SystemContext);
    const apiContext: ApiContextProps = useContext<ApiContextProps>(ApiContext);
    const tokenContext: TokenContextProps = useContext<TokenContextProps>(TokenContext);

    const inputRef: React.RefObject<HTMLInputElement> = useRef<HTMLInputElement>(null);

    const [description, setDescription] = useState<string>('');
    const [sendPending, setSendPending] = useState<boolean>(false);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const [successMessage, setSuccessMessage] = useState<string | null>(null);
    const [latestKeyEvent, setLatestKeyEvent] = useState<React.KeyboardEvent | null>(null);
    const [startPosition, setStartPosition] = useState<number | null>(null);

    const [windowSize, setWindowSize] = useState<[number, number]>([1920, 1080]);
    
    useEffect(() => {

        function updateSize(): void {
            setWindowSize([window.innerWidth, window.innerHeight]);
        }

        window.addEventListener('resize', updateSize);
        updateSize();
        return () => window.removeEventListener('resize', updateSize);
    }, []);

    useEffect(() => {

        if (latestKeyEvent !== null && startPosition !== null) {

            (latestKeyEvent.target as HTMLInputElement).selectionStart = startPosition + 1;
            (latestKeyEvent.target as HTMLInputElement).selectionEnd = startPosition + 1;
        }

    }, [latestKeyEvent, startPosition]);
    
    async function handleSendBugReport(): Promise<void> {

        setSendPending(true);

        const bugReports: BugReport[] = await indexedDBContext.indexedDBService.getAllBugReports();

        const fileContent: string = 'K-Fleet Experience PWA Bug Report\t' + appVersion + '\n\n' + [...bugReports].sort((a: BugReport, b: BugReport) => a.timestamp.getTime() - b.timestamp.getTime()).map((bugReport: BugReport) => {
            return datetimeToVisualString(bugReport.timestamp, false, systemContext.serverTimezoneOffsetMinutes ?? 0) + '\t' + bugReport.header + '\t' + bugReport.message;
        }).join('\n\n');

        const success: boolean = await apiContext.api?.sendBugReport(fileContent, description, tokenContext.token) ?? false;

        if (success) {
            setErrorMessage(null);
            setSuccessMessage('Bug report sent successfully');
        } else {
            setErrorMessage('Failed to send bug report. Make sure you are connected to the internet. Alternatively, you can download it and send it to K-Fleet Support in an email.');
            setSuccessMessage(null);
        }

        setSendPending(false);
    }

    async function handleDownloadBugReportFile(): Promise<void> {

        const bugReports: BugReport[] = await indexedDBContext.indexedDBService.getAllBugReports();

        const fileContent: string = 'K-Fleet Experience PWA Bug Report\t' + appVersion + '\n\n' + [...bugReports].sort((a: BugReport, b: BugReport) => a.timestamp.getTime() - b.timestamp.getTime()).map((bugReport: BugReport) => {
            return datetimeToVisualString(bugReport.timestamp, false, systemContext.serverTimezoneOffsetMinutes ?? 0) + '\t' + bugReport.header + '\t' + bugReport.message;
        }).join('\n\n');

        fileDownload(fileContent, 'KFleetExperienceBugReport.txt');
    }

    return (
        <Dialog 
            open={props.controller} 
            onClose={(): void => props.onClose()}
        >
            <DialogTitle>Send Bug Report</DialogTitle>
            <DialogContent>
                <Typography>
                    We are sorry to hear you are having difficulties with our application.
                </Typography>
                <br />
                <Typography>
                    Please describe the problem here, then click the button below to send it to the K-Fleet Support Team.
                </Typography>
                <br />
                <FormControl
                    onClick={(): void => inputRef.current?.focus()}
                    style={{
                        height: '150px',
                        width: Math.min(windowSize[0] * 0.65, 500),
                        overflowX: 'auto',
                        border: '1px solid #777777',
                        borderRadius: '5px',
                        cursor: 'text',
                    }}
                >
                    <Input
                        type='text'
                        value={description}
                        onChange={(e: React.ChangeEvent): void => setDescription((e.target as HTMLInputElement).value)}
                        onKeyDown={(e: React.KeyboardEvent): void => handleTab(e, description, setDescription, setLatestKeyEvent, setStartPosition)}
                        multiline={true}
                        disableUnderline={true}
                        inputRef={inputRef}
                    />
                </FormControl>
                <br />
                <br />
                <Typography>
                    <span style={{ fontWeight: 'bold' }}>Privacy Notice:</span> By sending this bug report, you accept that information about your device and K-Fleet usage relevant to the encountered bug will be sent to the K-Fleet Support Team. It will only be used for resolving the bug. We will treat this report as confidential, and never share it with any third parties. If you have any questions, please contact us at km.support.fleet@km.kongsberg.com.
                </Typography>
                <br />
                <div style={{
                    textAlign: 'center',
                }}>
                    <LoadingButton 
                        width='150px'
                        height='40px'
                        text='Send Bug Report'
                        controller={sendPending}
                        onClick={handleSendBugReport}
                        buttonColor='primary'
                        circularColor='error'
                        circularSize='30px'
                        variant='contained'
                    />
                </div>
                <br />
                <ErrorMessage
                    message={errorMessage}
                    maxWidth={Math.min(windowSize[0] * 0.8, 500)}
                />
                <SuccessMessage
                    message={successMessage}
                    maxWidth={Math.min(windowSize[0] * 0.8, 500)}
                />
                <br />
                <Typography>
                    Alternatively, you can download the bug report:
                </Typography>
                <br />
                <div style={{
                    textAlign: 'center',
                }}>
                    <Button
                        onClick={handleDownloadBugReportFile}
                        color='primary'
                        variant='contained'
                        style={{
                            width: '120px',
                            height: '32px',
                            lineHeight: 'normal',
                            marginRight: '10px',
                        }}
                    >
                        Download Bug Report
                    </Button>
                </div>
            </DialogContent>
            <DialogActions>
                <Button
                    onClick={(): void => props.onClose()}
                    color='error'
                    variant='contained'
                    style={{
                        width: '90px',
                        height: '32px',
                        lineHeight: 'normal',
                        marginRight: '10px',
                    }}
                >
                    Close
                </Button>
            </DialogActions>
        </Dialog>
    );
}

export default BugReportDialog;